import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SignatureCanvas from 'react-signature-canvas';
import { isSignatureValid } from '../utils/signatureValidation';
import {
  AVONDALE_HANDBOOK,
  AVONDALE_HANDBOOK_ACKNOWLEDGE_ENDPOINT,
  AVONDALE_HANDBOOK_ATTESTATION,
  AVONDALE_HANDBOOK_CAREGIVER_ENDPOINT,
  AVONDALE_HANDBOOK_DOCUMENT_ENDPOINT,
  type AvondaleHandbookAcknowledgeRequest,
  type AvondaleHandbookAcknowledgeResponse,
  type AvondaleHandbookCaregiverResponse,
} from '../../shared/avondaleHandbook';

// pdf.js is only needed if the caregiver chooses to open the handbook.
const HandbookPdfViewer = lazy(() => import('../components/HandbookPdfViewer'));

const AVONDALE_PURPLE = '#702b9d';
const AVONDALE_PURPLE_DARK = '#330662';
const HANDBOOK_DOWNLOAD_FILENAME = 'Avondale HHA-PCA Handbook.pdf';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function handbookDocumentUrl(revision: string, download = false): string {
  const params = new URLSearchParams({ revision });
  if (download) params.set('download', '1');
  return `${AVONDALE_HANDBOOK_DOCUMENT_ENDPOINT}?${params.toString()}`;
}

/**
 * The site background is painted on <body> with `background-size: cover`. This page is over
 * 100 screens tall, so "cover" would stretch the photo to the full document height and leave
 * only a zoomed-in sliver visible. Paint the same image on a fixed, viewport-sized layer
 * instead (works on iOS, where `background-attachment: fixed` is ignored).
 */
function FixedBackdrop() {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: -1,
        backgroundImage: 'var(--app-background-image, none)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    />
  );
}

function formatIsoDate(isoDate: string): string {
  const [year, month, day] = isoDate.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function ViewerFallback() {
  return (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#25232b' }}>
      <CircularProgress sx={{ color: 'rgba(255,255,255,0.85)' }} />
    </Box>
  );
}

function isAvondaleDomain(): boolean {
  const hostname = window.location.hostname;
  return hostname === 'portal.avondale.pro' || hostname === 'localhost' || hostname === '127.0.0.1';
}

function revokeUrl(url: string | null): void {
  if (url) URL.revokeObjectURL(url);
}

function pdfObjectUrlFromBase64(base64: string): string {
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
}

type CaregiverLookup =
  | { status: 'loading' }
  | { status: 'ready'; caregiver: AvondaleHandbookCaregiverResponse }
  | { status: 'error'; message: string; retryable: boolean };

const MISSING_ID_MESSAGE = 'This link is missing your caregiver record. Please ask Avondale HR to resend your handbook link.';

/** Resolves the `nexusId` in the link against the caregiver's real Avondale Nexus record. */
function useCaregiverLookup(nexusId: string): [CaregiverLookup, () => void] {
  const [lookup, setLookup] = useState<CaregiverLookup>(
    nexusId ? { status: 'loading' } : { status: 'error', message: MISSING_ID_MESSAGE, retryable: false }
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!nexusId) return undefined;
    let cancelled = false;
    setLookup({ status: 'loading' });
    (async () => {
      try {
        const response = await fetch(`${AVONDALE_HANDBOOK_CAREGIVER_ENDPOINT}/${encodeURIComponent(nexusId)}`, {
          headers: { Accept: 'application/json' },
        });
        const data = await response.json().catch(() => ({})) as Partial<AvondaleHandbookCaregiverResponse> & { message?: string };
        if (cancelled) return;
        if (!response.ok || !data.nexusId) {
          setLookup({
            status: 'error',
            message: data.message || 'We could not verify this link. Please ask Avondale HR to resend it.',
            retryable: response.status >= 500 || response.status === 429,
          });
          return;
        }
        setLookup({ status: 'ready', caregiver: data as AvondaleHandbookCaregiverResponse });
      } catch {
        if (cancelled) return;
        setLookup({
          status: 'error',
          message: 'We could not reach the server to verify this link. Check your connection and try again.',
          retryable: true,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nexusId, attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  return [lookup, retry];
}

export default function AvondaleHandbook() {
  const theme = useTheme();
  const mobileViewer = useMediaQuery(theme.breakpoints.down('sm'));
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const nexusId = (params.get('nexusId') || '').trim();
  const [lookup, retryLookup] = useCaregiverLookup(nexusId);
  const caregiver = lookup.status === 'ready' ? lookup.caregiver : null;
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerError, setViewerError] = useState('');
  const [viewerAttempt, setViewerAttempt] = useState(0);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [signature, setSignature] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<AvondaleHandbookAcknowledgeResponse | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const startedAtRef = useRef<number>(Date.now());
  const errorAlertRef = useRef<HTMLDivElement | null>(null);
  const signaturePadRef = useRef<SignatureCanvas | null>(null);
  const signatureContainerRef = useRef<HTMLDivElement | null>(null);
  const handbookUrl = caregiver ? handbookDocumentUrl(caregiver.handbookRevision) : AVONDALE_HANDBOOK_DOCUMENT_ENDPOINT;
  const handbookDownloadUrl = caregiver
    ? handbookDocumentUrl(caregiver.handbookRevision, true)
    : `${AVONDALE_HANDBOOK_DOCUMENT_ENDPOINT}?download=1`;

  useEffect(() => () => revokeUrl(generatedUrl), [generatedUrl]);

  // Size the signature pad's backing store to its box (and keep strokes on resize).
  useEffect(() => {
    if (result) return undefined;
    function resizeSignatureCanvas(): void {
      const pad = signaturePadRef.current;
      const container = signatureContainerRef.current;
      if (!pad || !container) return;
      const canvas = pad.getCanvas();
      const { width, height } = container.getBoundingClientRect();
      if (!width || !height) return;
      const data = pad.toData();
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.width = width;
      canvas.height = height;
      pad.clear();
      if (data.length) pad.fromData(data);
    }
    const frame = window.requestAnimationFrame(resizeSignatureCanvas);
    window.addEventListener('resize', resizeSignatureCanvas);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', resizeSignatureCanvas);
    };
  }, [result]);

  function clearSignature(): void {
    signaturePadRef.current?.clear();
    setSignature('');
  }

  function validate(): boolean {
    if (!caregiver) {
      setError(MISSING_ID_MESSAGE);
      return false;
    }
    if (caregiver.emailRequired && !EMAIL_PATTERN.test(email.trim())) {
      setEmailError('Please enter a valid email address.');
      setError('Please enter a valid email address so we can send your signed handbook.');
      window.requestAnimationFrame(() => errorAlertRef.current?.focus());
      return false;
    }
    if (!isSignatureValid(signaturePadRef.current, signature)) {
      setError('Please sign in the signature box.');
      return false;
    }
    return true;
  }

  async function submit(): Promise<void> {
    setError('');
    if (!validate() || !caregiver) return;
    setSubmitting(true);
    try {
      const payload: AvondaleHandbookAcknowledgeRequest = {
        nexusId: caregiver.nexusId,
        handbookRevision: caregiver.handbookRevision,
        signature,
        attested: true,
        readingSeconds: Math.max(0, Math.round((Date.now() - startedAtRef.current) / 1000)),
        email: caregiver.emailRequired ? email.trim() : undefined,
      };
      const response = await fetch(AVONDALE_HANDBOOK_ACKNOWLEDGE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({})) as Partial<AvondaleHandbookAcknowledgeResponse> & { message?: string };
      if (!response.ok && response.status !== 207) {
        throw new Error(data.message || 'We could not submit your acknowledgment. Please try again.');
      }
      if (data.pdfBase64) {
        setGeneratedUrl(pdfObjectUrlFromBase64(data.pdfBase64));
      }
      setResult(data as AvondaleHandbookAcknowledgeResponse);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'We could not submit your acknowledgment.');
    } finally {
      setSubmitting(false);
    }
  }

  if (!isAvondaleDomain()) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, md: 4 } }}>
        <Alert severity="error">This handbook page is only available from the Avondale portal address.</Alert>
      </Box>
    );
  }

  if (lookup.status !== 'ready' || !caregiver) {
    return (
      <Container maxWidth="sm" sx={{ mt: { xs: 4, sm: 8 }, mb: 5 }}>
        <FixedBackdrop />
        <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            {AVONDALE_HANDBOOK.organization} {AVONDALE_HANDBOOK.title}
          </Typography>
          {lookup.status !== 'error' ? (
            <>
              <CircularProgress sx={{ my: 3, color: AVONDALE_PURPLE }} />
              <Typography color="text.secondary">Verifying your handbook link...</Typography>
            </>
          ) : (
            <>
              <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>{lookup.message}</Alert>
              {lookup.retryable && (
                <Button variant="outlined" onClick={retryLookup} sx={{ mt: 3 }}>
                  Try again
                </Button>
              )}
            </>
          )}
        </Paper>
      </Container>
    );
  }

  // One signature per caregiver. A spent link gets a short notice and can download the
  // unsigned handbook, but the signed document remains in Nexus for HR.
  if (!result && caregiver.previousAcknowledgedDate) {
    return (
      <Container maxWidth="sm" sx={{ mt: { xs: 4, sm: 8 }, mb: 5 }}>
        <FixedBackdrop />
        <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 56, color: '#8900e1', mb: 1 }} />
          <Typography variant="h5" gutterBottom>
            Handbook already acknowledged
          </Typography>
          <Typography color="text.secondary">
            Our records show you signed the {AVONDALE_HANDBOOK.title} acknowledgment on{' '}
            {formatIsoDate(caregiver.previousAcknowledgedDate)}. There is nothing more to do.
          </Typography>
          <Button
            component="a"
            href={handbookDownloadUrl}
            download={HANDBOOK_DOWNLOAD_FILENAME}
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{
              mt: 3,
              color: AVONDALE_PURPLE,
              borderColor: AVONDALE_PURPLE,
              fontWeight: 700,
              '&:hover': { borderColor: AVONDALE_PURPLE_DARK, bgcolor: 'rgba(112, 43, 157, 0.04)' },
            }}
          >
            Download the full handbook
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Think this is a mistake? Contact Avondale HR.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (result) {
    return (
      <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
        <FixedBackdrop />
        <Paper elevation={3} sx={{ p: 3, bgcolor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: '#8900e1' }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Thank You!
            </Typography>
            <Typography variant="h6" color="text.secondary" align="center">
              {result.message}
            </Typography>
          </Box>
          {result.nexusStamped === false && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your signed acknowledgment was saved, but your Avondale record could not be updated automatically yet.
              Please contact Avondale HR to finish updating your record.
            </Alert>
          )}
          {result.emailSent === false && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              We could not email your signed handbook. Please download your copy below or contact Avondale HR.
            </Alert>
          )}
          {generatedUrl && (
            <Box>
              <Typography variant="h6" align="center" gutterBottom>
                Your Signed Handbook
              </Typography>
              <Paper elevation={2} sx={{ p: 2, bgcolor: 'white' }}>
                <Box
                  component="iframe"
                  src={generatedUrl}
                  title="Signed handbook"
                  sx={{ width: '100%', minHeight: { xs: 520, md: 900 }, border: 0 }}
                />
              </Paper>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = generatedUrl;
                    link.download = `${caregiver.fullName || 'Avondale'}_HHA-PCA_Handbook_Signed.pdf`;
                    link.click();
                  }}
                >
                  Download signed handbook
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
      <FixedBackdrop />
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ mt: 1 }}>
            Acknowledgment of Receipt
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" paragraph>
            {AVONDALE_HANDBOOK.organization} {AVONDALE_HANDBOOK.title} · {AVONDALE_HANDBOOK.version} edition
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => {
                setViewerError('');
                setViewerOpen(true);
              }}
              sx={{
                color: AVONDALE_PURPLE,
                borderColor: AVONDALE_PURPLE,
                fontWeight: 700,
                '&:hover': { borderColor: AVONDALE_PURPLE_DARK, bgcolor: 'rgba(112, 43, 157, 0.04)' },
              }}
            >
              View handbook
            </Button>
          </Box>

          {caregiver.emailRequired && (
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email address"
                value={email}
                error={Boolean(emailError)}
                helperText={emailError}
                autoComplete="email"
                onChange={(event) => {
                  setEmail(event.target.value);
                  setEmailError('');
                  setError('');
                }}
              />
            </Box>
          )}

          <Typography variant="body1">
            {AVONDALE_HANDBOOK_ATTESTATION}
          </Typography>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Signature
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please sign in the box below
            </Typography>
            <Box ref={signatureContainerRef} sx={{ border: '1px solid #000000', width: '100%', height: 200, mb: 2 }}>
              <SignatureCanvas
                ref={signaturePadRef}
                onEnd={() => {
                  const pad = signaturePadRef.current;
                  if (!pad || pad.isEmpty()) return;
                  setSignature(pad.getCanvas().toDataURL('image/png'));
                  setError('');
                }}
                canvasProps={{ className: 'signatureCanvas', style: { width: '100%', height: '100%' } }}
              />
            </Box>

            {error && (
              <Alert ref={errorAlertRef} tabIndex={-1} severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="outlined" color="secondary" onClick={clearSignature} disabled={submitting}>
                Clear Signature
              </Button>
              <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                disabled={submitting}
                onClick={submit}
              >
                {submitting ? 'Submitting...' : 'Sign and Submit'}
              </Button>
            </Box>
          </Box>
      </Paper>

      <Dialog
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={mobileViewer}
        aria-label={AVONDALE_HANDBOOK.title}
      >
        <DialogTitle sx={{ display: { xs: 'none', sm: 'block' } }}>{AVONDALE_HANDBOOK.title}</DialogTitle>
        <DialogContent
          dividers
          sx={{ height: { xs: 'calc(100dvh - 52px)', sm: 680 }, minHeight: 0, p: 0, overflow: 'hidden' }}
        >
          {viewerError ? (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
              <Alert
                severity="error"
                action={(
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setViewerError('');
                      setViewerAttempt((attempt) => attempt + 1);
                    }}
                  >
                    Try again
                  </Button>
                )}
              >
                {viewerError}
              </Alert>
            </Box>
          ) : (
            <Suspense fallback={<ViewerFallback />}>
              <HandbookPdfViewer
                key={viewerAttempt}
                url={handbookUrl}
                title={`${AVONDALE_HANDBOOK.organization} ${AVONDALE_HANDBOOK.title}`}
                subtitle={`${AVONDALE_HANDBOOK.version} edition`}
                enforceSequentialReading={false}
                onError={setViewerError}
              />
            </Suspense>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            component="a"
            href={handbookDownloadUrl}
            download={HANDBOOK_DOWNLOAD_FILENAME}
            startIcon={<DownloadIcon />}
          >
            Download PDF
          </Button>
          <Button onClick={() => setViewerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
